import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { FollowUp } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

const EMPTY: FollowUp = { questions: [], keyVerse: null, groupGuide: null, prayer: null, challenge: null };

/** Texto para compartir por WhatsApp (líderes de célula, familias). */
export function followUpText(title: string, f: FollowUp): string {
  return [
    `*Continuar el domingo · ${title}*`,
    f.keyVerse ? `📖 ${f.keyVerse}` : null,
    f.questions.length ? `*Para pensar esta semana*\n${f.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}` : null,
    f.groupGuide ? `*En la célula o en familia*\n${f.groupGuide}` : null,
    f.prayer ? `*Oración*\n${f.prayer}` : null,
    f.challenge ? `*Reto de la semana*\n${f.challenge}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');
}

/** «Continuar el domingo» al final de lo publicado: preguntas, guía para célula o familia, oración y reto. */
export function FollowUpSection({ title, followUp: f }: { title: string; followUp: FollowUp }) {
  return (
    <Card title="Continuar el domingo">
      {f.keyVerse ? (
        <p>
          <strong>Versículo clave:</strong> {f.keyVerse}
        </p>
      ) : null}
      {f.questions.length ? (
        <>
          <h3 className="small">Para pensar esta semana</h3>
          <ol>
            {f.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </>
      ) : null}
      {f.groupGuide ? (
        <>
          <h3 className="small">En la célula o en familia</h3>
          <p className="pre-line">{f.groupGuide}</p>
        </>
      ) : null}
      {f.prayer ? (
        <>
          <h3 className="small">Oración</h3>
          <p className="pre-line">{f.prayer}</p>
        </>
      ) : null}
      {f.challenge ? (
        <>
          <h3 className="small">Reto de la semana</h3>
          <p className="pre-line">{f.challenge}</p>
        </>
      ) : null}
      <div className="actions">
        <a className="btn btn--secondary" href={`https://wa.me/?text=${encodeURIComponent(followUpText(title, f))}`} target="_blank" rel="noreferrer">
          Compartir por WhatsApp
        </a>
        <Button variant="ghost" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
    </Card>
  );
}

/** Quien prepara la predicación llena «Continuar el domingo». Se ve de inmediato en lo publicado. */
export function FollowUpCard({ churchId, pieceId, canEdit }: { churchId: string; pieceId: string; canEdit: boolean }) {
  const url = `/content/churches/${churchId}/pieces/${pieceId}/follow-up`;
  const data = useAsync((signal) => api.get<{ followUp: FollowUp | null }>(url, signal).then((r) => r.followUp ?? EMPTY), [url]);
  const [f, setF] = useState<FollowUp>(EMPTY);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const loaded = data.status === 'ready' ? data.data : null;
  useEffect(() => {
    if (loaded) setF({ ...loaded, questions: [...loaded.questions, ...Array(Math.max(0, 4 - loaded.questions.length)).fill('')] });
  }, [loaded]);
  if (!loaded) return null;
  if (!canEdit && loaded === EMPTY) return null;

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.put(url, { ...f, questions: f.questions.map((q) => q.trim()).filter(Boolean) });
      setMsg({ tone: 'success', text: 'Guardado. Ya se ve en lo publicado.' });
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }
  const set = (k: keyof Omit<FollowUp, 'questions'>) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value || null });

  return (
    <Card title="Continuar el domingo">
      <p className="muted small">Opcional. Para que el mensaje siga durante la semana: en las células y en casa.</p>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <Field label="Versículo clave" value={f.keyVerse ?? ''} maxLength={120} disabled={!canEdit} placeholder="Santiago 2:17" onChange={set('keyVerse')} />
      <fieldset className="choices">
        <legend>Preguntas para pensar esta semana</legend>
        {f.questions.map((q, i) => (
          <Field
            key={i}
            label={`Pregunta ${i + 1}`}
            value={q}
            maxLength={300}
            disabled={!canEdit}
            onChange={(e) => setF({ ...f, questions: f.questions.map((x, j) => (j === i ? e.target.value : x)) })}
          />
        ))}
      </fieldset>
      <div className="field">
        <label htmlFor="fu-guide">Guía para la célula o la familia</label>
        <textarea id="fu-guide" rows={4} maxLength={2000} disabled={!canEdit} value={f.groupGuide ?? ''} onChange={set('groupGuide')} placeholder="Lean juntos… Platiquen…" />
      </div>
      <div className="field">
        <label htmlFor="fu-prayer">Oración</label>
        <textarea id="fu-prayer" rows={2} maxLength={1000} disabled={!canEdit} value={f.prayer ?? ''} onChange={set('prayer')} />
      </div>
      <Field label="Reto de la semana" value={f.challenge ?? ''} maxLength={500} disabled={!canEdit} placeholder="Esta semana llama a alguien que se ha alejado." onChange={set('challenge')} />
      {canEdit ? (
        <Button busy={busy} onClick={() => void save()}>
          Guardar «Continuar el domingo»
        </Button>
      ) : null}
    </Card>
  );
}
